/** Central total delivery duration. The absent-status path stays in the original engine. */
(() => {
  'use strict';
  const calendar = typeof module !== 'undefined' && module.exports
    ? require('./delivery-calendar.js') : window.DeliveryCalendar;
  const engine = typeof module !== 'undefined' && module.exports
    ? require('./delivery-engine.js') : window.DeliveryEngine;

  function whole(value, name, minimum = 0) {
    if (!Number.isSafeInteger(value) || value < minimum) throw new Error(`Invalid ${name}`);
    return value;
  }

  // Offset-free values belong to the profile clock, never the visitor's timezone.
  function expiryInstant(value, timezone) {
    if (typeof value !== 'string') throw new Error('Invalid central expiry');
    const parts = value.match(/^(\d{4}-\d{2}-\d{2})(?:[T ]([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d)(\.\d{1,3})?)?(?: ?(Z|[+-](?:[01]\d|2[0-3]):?[0-5]\d))?)?$/);
    if (!parts) throw new Error('Invalid central expiry');
    const day = calendar.date(parts[1]);
    if (parts[6]) {
      const offset = parts[6].replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
      const result = new Date(`${parts[1]}T${parts[2]}:${parts[3]}:${parts[4] || '00'}${parts[5] || ''}${offset}`);
      if (!Number.isFinite(result.valueOf())) throw new Error('Invalid central expiry');
      return result;
    }
    if (!timezone) throw new Error('Missing profile timezone for central expiry');
    const formatter = new Intl.DateTimeFormat('en-GB', { timeZone: timezone,
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit',
      minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
    const civil = instant => {
      const p = Object.fromEntries(formatter.formatToParts(new Date(instant)).map(part => [part.type, part.value]));
      return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    };
    // A date-only value includes the entire local day, even on a 23/25-hour day.
    const milliseconds = Math.round(Number(parts[5] || 0) * 1000);
    const target = parts[2] === undefined
      ? Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate() + 1)
      : Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), +parts[2], +parts[3], +(parts[4] || 0));
    const offsets = new Set([-36, 0, 36].map(hours => {
      const sample = target + hours * 3600000;
      return civil(sample) - sample;
    }));
    const matches = [...offsets].map(offset => target - offset).filter(instant => civil(instant) === target);
    if (!matches.length) throw new Error('Central expiry is a nonexistent local time');
    // In an autumn overlap expiry uses the last occurrence of the local time.
    return new Date(Math.max(...matches) + milliseconds);
  }

  function countryOverride(raw, country) {
    if (raw.countries == null) return null;
    if (!Array.isArray(raw.countries)) throw new Error('Invalid central country overrides');
    const code = typeof country === 'string' ? country.trim().toUpperCase() : '';
    if (!raw.countries.length) return null;
    if (!/^[A-Z]{2}$/.test(code)) throw new Error('Missing destination country');
    const matches = raw.countries.filter(entry => {
      if (!entry || typeof entry.land !== 'string' || !/^[A-Z]{2}$/.test(entry.land.trim().toUpperCase())) {
        throw new Error('Invalid country override code');
      }
      return entry.land.trim().toUpperCase() === code;
    });
    if (matches.length > 1) throw new Error('Ambiguous country override');
    if (!matches.length) return null;
    const entry = matches[0];
    const min = whole(entry.min_werktage, 'country minimum', 1);
    const max = whole(entry.max_werktage, 'country maximum', 1);
    if (min > max || !Number.isSafeInteger(entry.zusatztage)) throw new Error('Invalid country delivery override');
    return { min, max, extra: entry.zusatztage };
  }

  // The duration standard also applies when no legacy country row was created.
  // Explicit opt-outs and ambiguous legacy configuration still take precedence.
  function countryRule(profile, rules, country, central) {
    if (!Array.isArray(rules)) throw new Error('Invalid delivery country rules');
    const matches = rules.filter(rule => rule.country === country);
    if (matches.length > 1) throw new Error('Ambiguous delivery country rule');
    if (matches.length) return matches[0].enabled ? matches[0] : null;
    return central ? { enabled: true, country, region: '',
      deliveryWeekdays: profile.productionWeekdays, blockedDates: [], extraDays: 0 } : null;
  }

  function resolve(raw, now, timezone, country) {
    if (raw == null) return null;
    const selected = countryOverride(raw, country);
    const min = whole(selected ? selected.min : raw.minDays, 'central minimum', 1);
    const max = whole(selected ? selected.max : raw.maxDays, 'central maximum', 1);
    if (min > max) throw new Error('Central minimum exceeds maximum');
    const calculationMode = raw.calculationMode ?? 'gesamt';
    if (!['gesamt', 'profil'].includes(calculationMode)) throw new Error('Invalid delivery calculation mode');
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(raw.cutoff)) throw new Error('Invalid central cutoff');
    if (!['normal', 'pausiert'].includes(raw.mode)) throw new Error('Invalid central mode');
    if (!Number.isSafeInteger(raw.extraDays)) throw new Error('Invalid central additional days');
    let expired = false;
    if (raw.validUntil) {
      const expiry = expiryInstant(raw.validUntil, timezone);
      expired = now >= expiry;
    }
    // Owner decision: global delays apply to every country; its own delay is additive.
    const extra = expired ? 0 : raw.extraDays + (selected ? selected.extra : 0);
    if (!Number.isSafeInteger(extra)) throw new Error('Invalid total additional days');
    if (!Number.isSafeInteger(min + extra) || !Number.isSafeInteger(max + extra) || min + extra < 1) throw new Error('Central reduction must leave at least one working day');
    return { min, max, extra, calculationMode, cutoff: raw.cutoff, mode: expired ? 'normal' : raw.mode,
      notice: typeof raw.notice === 'string' ? raw.notice : '' };
  }

  function calculate(profile, rule, now, status) {
    if (status.mode === 'pausiert') return { paused: true, notice: status.notice };
    // F-208, owner 21.10.2026: production and delivery use separate calendars;
    // Saturday delivery must survive central administration without changing dates.
    // Explicit migration mode reuses F-57; the fixed-span total mode stays unchanged.
    if (status.calculationMode === 'profil') {
      const year = engine.clock(now, profile.timezone).date.getUTCFullYear();
      const holidays = (country, region) => new Set([year, year + 1, year + 2]
        .flatMap(value => [...calendar.localHolidays(country, region, value)]));
      const result = engine.calculate({ ...profile, cutoff: status.cutoff }, {
        ...rule, totalMin: status.min, totalMax: status.max,
        extraDays: whole(rule.extraDays ?? 0, 'country additional days') + status.extra
      }, now, holidays(profile.country, profile.region), holidays(rule.country, rule.region));
      return { ...result, paused: false, notice: status.extra > 0 ? status.notice : '' };
    }
    const local = engine.clock(now, profile.timezone);
    const days = values => {
      if (!Array.isArray(values) || !values.length || values.some(value => !/^[0-6]$/.test(String(value)))) throw new Error('Invalid working weekdays');
      return new Set(values.map(Number));
    };
    const production = days(profile.productionWeekdays), delivery = days(rule.deliveryWeekdays);
    // One common calendar is essential: every +1 moves both endpoints by the same working day.
    const weekdays = new Set([...production].filter(day => day > 0 && day < 6 && delivery.has(day)));
    if (!weekdays.size) throw new Error('No common working weekday');
    const blocked = new Set([...(profile.blockedDates || []), ...(rule.blockedDates || [])]
      .map(value => calendar.iso(calendar.date(value))));
    const vacations = (profile.vacations || []).map(range => {
      const from = calendar.date(range.from), until = calendar.date(range.until);
      if (from > until) throw new Error('Invalid vacation range');
      return { from, until };
    });
    const holidays = new Map();
    const working = day => {
      const year = day.getUTCFullYear();
      if (!holidays.has(year)) holidays.set(year, new Set([
        ...calendar.localHolidays(profile.country, profile.region, year),
        ...calendar.localHolidays(rule.country, rule.region, year)
      ]));
      return weekdays.has(day.getUTCDay()) && !holidays.get(year).has(calendar.iso(day)) &&
        !blocked.has(calendar.iso(day)) && !vacations.some(range => day >= range.from && day <= range.until);
    };
    const next = day => { while (!working(day)) day = calendar.add(day, 1); return day; };
    const advance = (day, count) => {
      for (let i = 0; i < count; i++) day = next(calendar.add(day, 1));
      return day;
    };
    const [hour, minute] = status.cutoff.split(':').map(Number);
    const start = next(local.minutes >= hour * 60 + minute ? calendar.add(local.date, 1) : local.date);
    let delay = whole(rule.extraDays ?? 0, 'country additional days');
    if (rule.temporaryEnabled) {
      const from = rule.delayFrom ? calendar.iso(calendar.date(rule.delayFrom)) : null;
      const until = rule.delayUntil ? calendar.iso(calendar.date(rule.delayUntil)) : null;
      if (from && until && from > until) throw new Error('Invalid country delay range');
      const today = calendar.iso(local.date);
      if ((!from || today >= from) && (!until || today <= until)) delay += whole(rule.temporaryDays, 'country temporary delay');
    }
    const first = status.min + status.extra + delay, last = status.max + status.extra + delay;
    if (!Number.isSafeInteger(first) || !Number.isSafeInteger(last)) throw new Error('Invalid delivery horizon');
    return { min: calendar.iso(advance(start, first)), max: calendar.iso(advance(start, last)),
      start: calendar.iso(start), paused: false, notice: status.extra > 0 ? status.notice : '' };
  }

  function formatRange(result, locale) {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
    const first = formatter.format(calendar.date(result.min)), last = formatter.format(calendar.date(result.max));
    return result.min === result.max ? first
      : new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format([first, last]);
  }

  const api = { resolve, calculate, formatRange, countryOverride, countryRule };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else window.FizzyDeliveryStatus = window.FizzyDeliveryStatus || api;
})();
